/* /assets/os-product-form.js
 *
 * OS CUSTOM (upgrade-safe, replaces stock behaviour lost in the 8.1 upgrade -
 * see docs/UPGRADE-REAPPLY-CHECKLIST.md).
 *
 * Pipeline 7.6.0's stock theme.js registered <product-form> as a real custom
 * element (`ProductForm` class, theme.js). On every variant change it:
 *   - toggled `variant--soldout` / `variant--unavailable` on the nearest
 *     [data-product-form-wrapper] ancestor (sections/product.liquid's
 *     .product__inner - present, unchanged, on both 7.6.0 and 8.1)
 *   - updated the Add to Cart button's disabled state and its
 *     [data-add-to-cart-text] label text
 *   - dispatched a bubbling `theme:variant:change` CustomEvent
 *
 * Klaviyo's onsite embed reacts to that state (the `variant--soldout`
 * class and/or the event) to reveal its .klaviyo-bis-trigger "Notify Me
 * When Available" link in place of the Add to Cart button.
 *
 * Pipeline 8.1's stock theme.js dropped the <product-form> custom element
 * entirely (confirmed: diffed every customElements.define(...) call in
 * both versions' stock theme.js - 'product-form' is the only one removed).
 * Nothing else in 8.1 replaces this job. This file replaces ONLY that one
 * piece of behaviour, in a new OS-owned asset, without touching stock
 * theme.js.
 *
 * Important structural note: snippets/product-variant-options.liquid's
 * radio/select inputs are NOT DOM descendants of the <form> - they use
 * the HTML5 form="{{ uniq_id }}" attribute to associate with it from
 * elsewhere in the section (same pattern Shopify/Pipeline uses natively).
 * So this can't just do form.querySelectorAll(...) for the inputs; it has
 * to look them up via document-wide [form="<id>"] queries instead.
 *
 * Works via event delegation on document (not per-element listeners), so
 * it keeps working even if a variant change causes the surrounding markup
 * to be replaced (Shopify Section Rendering API navigation) - there's
 * nothing to re-attach.
 *
 * Root cause of the "class toggle doesn't survive a second variant change"
 * bug (see docs/UPGRADE-REAPPLY-CHECKLIST.md): 8.1's productForm() Alpine
 * component (theme.js) drives changeVariant() -> $sectionApi.navigate(),
 * which morphs the product section against freshly fetched server HTML on
 * EVERY variant change (Alpine.morph(this.rootEl, ...), theme.js). Nothing
 * server-side ever renders variant--soldout, so the morph patches that
 * class straight back off [data-product-form-wrapper] moments after this
 * script's synchronous `change` handler adds it - the button LABEL still
 * ends up correct (the server-rendered text is already availability-aware)
 * while the class silently reverts. theme.js dispatches a first-party
 * `theme:section:update` CustomEvent on `window` right after each morph/
 * replace completes, so this re-runs updateFormState() for every product
 * form on that event too, not just on `change` - re-asserting state after
 * the morph settles rather than racing it. Always re-queries the DOM fresh
 * rather than caching wrapper/form references, so this is correct whether
 * one or several product-form instances are present.
 *
 * Two variant sources: on load and after a section update, the hidden
 * input[name="id"] and the checked option radios are BOTH already correct
 * and BOTH come from the same Liquid source (current_variant =
 * product.selected_or_first_available_variant, in both product-buttons.liquid
 * and product-variant-options.liquid) - so this trusts the hidden input
 * directly (a straight id lookup in the variants array) rather than
 * re-deriving the variant from the radios, sidestepping any radio-matching
 * edge case entirely for those two paths. Radio-matching is only needed - and
 * only used - on a live `change` event, where the user just clicked a new
 * option and the hidden input is the thing that's stale.
 */
(function () {
  'use strict';

  var SELECTORS = {
    form: '[data-product-form]',
    formWrapper: '[data-product-form-wrapper]',
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    productJson: '[data-product-json]',
    variantInput: 'input[name="id"]'
  };

  var CLASSES = {
    soldOut: 'variant--soldout',
    unavailable: 'variant--unavailable'
  };

  function getVariants(form) {
    var script = form.querySelector(SELECTORS.productJson);
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent);
      return data && data.variants ? data.variants : null;
    } catch (e) {
      return null;
    }
  }

  // Finds every option input/select associated with this form via the
  // HTML5 form="" attribute (they live outside the <form> in the DOM),
  // groups them by their [data-option-position] ancestor
  // (.selector-wrapper, from product-variant-options.liquid), and reads
  // the checked/selected value for each position.
  function getSelectedOptions(form) {
    if (!form.id) return [];
    var associated = document.querySelectorAll('[form="' + form.id + '"]');
    var byPosition = {};
    associated.forEach(function (el) {
      var wrapper = el.closest('[data-option-position]');
      if (!wrapper) return;
      var position = wrapper.getAttribute('data-option-position');
      var value = null;
      if (el.tagName === 'SELECT') {
        value = el.value;
      } else if (el.type === 'radio') {
        if (el.checked) value = el.value;
      }
      if (value !== null && value !== undefined) byPosition[position] = value;
    });
    var positions = Object.keys(byPosition).sort(function (a, b) {
      return parseInt(a, 10) - parseInt(b, 10);
    });
    return positions.map(function (position) {
      return byPosition[position];
    });
  }

  function findMatchingVariant(variants, selectedOptions) {
    if (!selectedOptions.length) return null;
    return variants.find(function (variant) {
      return variant.options.every(function (option, index) {
        return selectedOptions[index] === undefined || selectedOptions[index] === option;
      });
    }) || null;
  }

  function findVariantById(variants, id) {
    var idStr = String(id);
    return variants.find(function (variant) {
      return String(variant.id) === idStr;
    }) || null;
  }

  // trustVariantInput: true on load and after a section update, when the
  // hidden input[name="id"] is fresh, server-correct, and cheaper/more
  // robust to trust directly than re-deriving via radio-matching (see file
  // header). Falls back to radio-matching if that lookup doesn't resolve.
  function updateFormState(form, trustVariantInput) {
    var variants = getVariants(form);
    if (!variants) return;

    var variantInput = form.querySelector(SELECTORS.variantInput);
    var variant = null;
    if (trustVariantInput && variantInput && variantInput.value) {
      variant = findVariantById(variants, variantInput.value);
    }
    if (!variant) {
      var selectedOptions = getSelectedOptions(form);
      variant = selectedOptions.length ? findMatchingVariant(variants, selectedOptions) : variants[0];
    }

    var wrapper = form.closest(SELECTORS.formWrapper);
    if (wrapper) {
      wrapper.classList.remove(CLASSES.soldOut, CLASSES.unavailable);
      if (variant) {
        if (!variant.available) wrapper.classList.add(CLASSES.soldOut);
      } else {
        wrapper.classList.add(CLASSES.unavailable);
      }
    }

    var addToCart = form.querySelector(SELECTORS.addToCart);
    if (addToCart) {
      addToCart.disabled = !(variant && variant.available);
    }

    var productForm = form.closest('product-form');
    var textAddToCart = productForm && productForm.getAttribute('data-text-add-to-cart');
    var textSoldOut = productForm && productForm.getAttribute('data-text-sold-out');
    var textUnavailable = productForm && productForm.getAttribute('data-text-unavailable');

    form.querySelectorAll(SELECTORS.addToCartText).forEach(function (el) {
      if (variant && textAddToCart && textSoldOut) {
        el.textContent = variant.available ? textAddToCart : textSoldOut;
      } else if (!variant && textUnavailable) {
        el.textContent = textUnavailable;
      }
    });

    if (variantInput && variant) {
      variantInput.value = variant.id;
    }

    form.dispatchEvent(new CustomEvent('theme:variant:change', {
      detail: { variant: variant },
      bubbles: true
    }));
  }

  function handleChange(event) {
    var target = event.target;
    if (!target || (target.tagName !== 'SELECT' && !(target.tagName === 'INPUT' && target.type === 'radio'))) return;
    var formId = target.getAttribute('form');
    var form = formId ? document.getElementById(formId) : target.closest(SELECTORS.form);
    if (!form || !form.matches(SELECTORS.form)) return;
    // The user just picked a new option - the hidden input still holds the
    // PREVIOUS variant's id, so this must derive the variant from the
    // radios/select, not trust the (stale) input.
    updateFormState(form, false);
  }

  document.addEventListener('change', handleChange);

  // Runs on load and after every Section Rendering API update (morph or
  // full replace) - trusts the hidden input[name="id"], which Liquid has
  // already resolved correctly in both cases (see file header). Needed on
  // load for the variant--soldout class, which nothing server-side ever
  // sets; needed after a morph because the morph patches that class straight
  // back off (see file header).
  function syncAll() {
    document.querySelectorAll(SELECTORS.form).forEach(function (form) {
      updateFormState(form, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  window.addEventListener('theme:section:update', syncAll);
})();
